import { useEffect } from "react";
import Hero from "./components/Hero";
import Experience from "./components/Experience";
import HowItWorks from "./components/HowItWorks";
import Logistics from "./components/Logistics";
import SocialProof from "./components/SocialProof";
import FAQ from "./components/FAQ";
import Footer from "./components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://missionbeachdive.com.au";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "SportsActivityLocation"],
  "name": "Mission Beach Dive Adventures",
  "description": "PADI-accredited scuba dive school and Great Barrier Reef tour operator based at Clump Point Jetty, Mission Beach, Queensland. Offering certified diver adventures, PADI Discover Scuba Diving, and guided snorkel reef day trips aboard MV Mahi-Mahi.",
  "url": siteUrl,
  "telephone": "0455957263",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Clump Point Jetty",
    "addressLocality": "Mission Beach",
    "addressRegion": "QLD",
    "postalCode": "4852",
    "addressCountry": "AU"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": -17.8614,
    "longitude": 146.1036
  },
  "areaServed": [
    { "@type": "Place", "name": "Bedarra Island" },
    { "@type": "Place", "name": "Mission Beach" },
    { "@type": "Place", "name": "Dunk Island" },
    { "@type": "Place", "name": "Cassowary Coast" },
    { "@type": "Place", "name": "Far North Queensland" }
  ],
  "hasMap": "https://maps.google.com/?q=Clump+Point+Jetty+Mission+Beach+QLD",
  "priceRange": "$$",
  "currenciesAccepted": "AUD",
  "paymentAccepted": "Cash, Credit Card",
  "sameAs": [
    "https://www.facebook.com/missionbeachdive",
    "https://www.instagram.com/missionbeachdive"
  ],
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      "opens": "07:00",
      "closes": "17:00"
    }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "249",
    "bestRating": "5",
    "worstRating": "1"
  }
};

const touristTripSchema = {
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  "name": "Great Barrier Reef Dive Day Trip from Bedarra Island",
  "description": "A full-day scuba diving and snorkelling reef trip departing Clump Point Jetty, Mission Beach, aboard MV Mahi-Mahi. Suitable for certified divers, beginners via PADI Discover Scuba Diving, and snorkellers. Ideal for guests staying at Bedarra Island Resort.",
  "url": `${siteUrl}/bedarra-island-scuba-diving`,
  "provider": {
    "@type": "LocalBusiness",
    "name": "Mission Beach Dive Adventures",
    "url": siteUrl
  },
  "touristType": ["Scuba Divers","Snorkellers","Adventure Travellers","Luxury Resort Guests"],
  "itinerary": {
    "@type": "ItemList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Depart Clump Point Jetty, Mission Beach aboard MV Mahi-Mahi" },
      { "@type": "ListItem", "position": 2, "name": "45–60 minute cruise to Beaver Reef and the outer Great Barrier Reef" },
      { "@type": "ListItem", "position": 3, "name": "Guided scuba dives or snorkel sessions on the outer reef" },
      { "@type": "ListItem", "position": 4, "name": "Return to Clump Point Jetty, Mission Beach" }
    ]
  },
  "offers": [
    {
      "@type": "Offer",
      "name": "All Day Snorkel Tour",
      "url": `${siteUrl}/bedarra-island-scuba-diving`,
      "price": "449",
      "priceCurrency": "AUD",
      "availability": "https://schema.org/InStock",
      "validFrom": "2026-01-01"
    },
    {
      "@type": "Offer",
      "name": "Discover Scuba Diving",
      "url": `${siteUrl}/bedarra-island-scuba-diving`,
      "price": "699",
      "priceCurrency": "AUD",
      "availability": "https://schema.org/InStock",
      "validFrom": "2026-01-01"
    },
    {
      "@type": "Offer",
      "name": "Certified Dive Tour",
      "url": `${siteUrl}/bedarra-island-scuba-diving`,
      "price": "799",
      "priceCurrency": "AUD",
      "availability": "https://schema.org/InStock",
      "validFrom": "2026-01-01"
    }
  ],
  "areaServed": {
    "@type": "Place",
    "name": "Bedarra Island, Great Barrier Reef, Queensland, Australia"
  },
  "location": {
    "@type": "Place",
    "name": "Beaver Reef, Outer Great Barrier Reef",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Mission Beach",
      "addressRegion": "QLD",
      "addressCountry": "AU"
    }
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Can I go scuba diving from Bedarra Island?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Bedarra Island Resort guests can take the Bedarra Water Taxi directly to Clump Point Jetty, Mission Beach and join Mission Beach Dive Adventures for a full dive or snorkel day trip on the outer Great Barrier Reef. We recommend booking in advance to secure your preferred date."
      }
    },
    {
      "@type": "Question",
      "name": "How far is the Great Barrier Reef from Bedarra Island?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The outer Great Barrier Reef is approximately 45–60 minutes by boat from Clump Point Jetty, Mission Beach aboard MV Mahi-Mahi. The water taxi from Bedarra Island travels directly to Clump Point Jetty — a scenic 20-minute crossing — making it an easy and rewarding day out from the resort."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to be a certified diver to join a reef trip?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Mission Beach Dive Adventures offers PADI Discover Scuba Diving for complete beginners — no prior experience required. Certified diver programs are available for Open Water and above, and guided snorkelling tours are also available for non-divers."
      }
    },
    {
      "@type": "Question",
      "name": "How much does a reef day trip from Bedarra Island cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "There are three tours to choose from. The All Day Snorkel Tour is $449 per person and includes snorkel gear, a guided snorkel session, lunch, reef tax, water taxi transfer, and underwater photos. The Discover Scuba Diving experience (no certification needed) is $699 per person and includes all dive equipment, a PADI instructor one-on-one, lunch, reef tax, and water taxi transfer. The Certified Dive Tour is $799 per person and covers two reef sites, all dive equipment and wetsuit, a PADI dive guide, lunch, reef tax, and water taxi transfer. All prices are per person and fully inclusive — no hidden extras."
      }
    },
    {
      "@type": "Question",
      "name": "What is included in a dive day trip with Mission Beach Dive Adventures?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Every tour includes all necessary equipment, reef tax / Marine Park fee, lunch and refreshments, and your water taxi transfer between Bedarra Island and Clump Point Jetty. Certified Dive Tours include a PADI dive guide and two reef site dives. Discover Scuba Diving includes a dedicated one-on-one PADI instructor. The All Day Snorkel Tour also includes underwater photos."
      }
    },
    {
      "@type": "Question",
      "name": "Is Mission Beach Dive Adventures PADI accredited?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Mission Beach Dive Adventures is a fully PADI-accredited dive school and reef tour operator, based at Clump Point Jetty, Mission Beach, Queensland. All dive guides and instructors hold current PADI certifications."
      }
    },
    {
      "@type": "Question",
      "name": "How do I book a scuba dive from Bedarra Island?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Book directly at missionbeachdive.com.au or call us on 0455 957 263. We recommend booking at least 48 hours in advance to secure your preferred date, particularly during peak season."
      }
    }
  ]
};

export default function BedarraIslandScubaDiving() {
  useEffect(() => {
    const schemas = [
      { id: "schema-local-business-bedarra", data: localBusinessSchema },
      { id: "schema-tourist-trip-bedarra", data: touristTripSchema },
      { id: "schema-faq-bedarra", data: faqSchema },
    ];

    schemas.forEach(({ id, data }) => {
      if (!document.getElementById(id)) {
        const script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      }
    });

    return () => {
      schemas.forEach(({ id }) => {
        const existing = document.getElementById(id);
        if (existing) existing.remove();
      });
    };
  }, []);

  return (
    <main>
      <Hero />
      <Experience />
      <HowItWorks />
      <Logistics />
      <SocialProof />
      <FAQ />
      <Footer />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp to book"
        className="whitespace-nowrap fixed bottom-7 right-7 z-50 flex items-center gap-2.5 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white pl-3.5 pr-5 py-3.5 rounded-full cursor-pointer transition-all duration-300 hover:scale-105 group"
        style={{ boxShadow: "0 4px 20px rgba(34,197,94,0.45)" }}
      >
        <span className="absolute inset-0 rounded-full bg-green-400 opacity-40 animate-ping pointer-events-none" />
        <div className="w-6 h-6 flex items-center justify-center shrink-0 relative">
          <i className="ri-whatsapp-line text-2xl" />
        </div>
        <span className="text-sm font-bold relative">Book via WhatsApp</span>
      </a>
    </main>
  );
}
